import React, { useState, useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { supabase } from './services/supabase';
import Dashboard from './components/Dashboard';
import LockDetail from './components/LockDetail';
import VisitorLock from './components/VisitorLock';
import LandingPage from './components/LandingPage';
import { Auth } from './components/Auth';
import { Profile } from './components/Profile';
import { AdminDashboard } from './components/AdminDashboard';
import { Privacy } from './components/Privacy';
import { Terms } from './components/Terms';
import { Loader2 } from 'lucide-react';

// Helper for protected routes
const ProtectedRoute = ({ children, session }: { children?: React.ReactNode, session: any }) => {
    const location = useLocation();
    if (!session) {
        return <Navigate to="/login" replace state={{ from: location }} />;
    }
    return <>{children}</>;
};

// Helper for Public Only routes (like Login when already logged in)
const PublicOnlyRoute = ({ children, session }: { children?: React.ReactNode, session: any }) => {
    if (session) {
        return <Navigate to="/dashboard" replace />;
    }
    return <>{children}</>;
};

const App: React.FC = () => {
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  
  // FIX: Safely access import.meta.env
  const env = (import.meta as any).env;
  const isProduction = env ? !env.DEV : false;

  useEffect(() => {
    // DOMAIN ENFORCEMENT: Prevent using the Vercel subdomain in production
    // If we are on a vercel.app domain, redirect to the custom domain
    const hostname = window.location.hostname;
    if (hostname.endsWith('.vercel.app')) {
        const newUrl = window.location.href.replace(hostname, 'lockd.in');
        window.location.replace(newUrl);
        return;
    }

    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        setSession(session);
        setLoading(false);
      })
      .catch((err) => {
        console.warn("Auth session check failed (network/CORS). Defaulting to logged out.", err);
        setSession(null);
        setLoading(false);
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      // Only set loading false if it was true (mostly handled by getSession, but good for safety)
      setLoading((prev) => (prev ? false : prev));
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading) {
      return (
          <div className="min-h-screen flex items-center justify-center bg-white">
              <Loader2 className="h-8 w-8 animate-spin text-slate-900" />
          </div>
      );
  }

  return (
    <HashRouter>
      <Routes>
        {/* Landing Page (Public) */}
        <Route 
            path="/" 
            element={
                session ? <Navigate to="/dashboard" replace /> : <LandingPage />
            } 
        />

        {/* Authentication (Public Only - Redirects to dashboard if logged in) */}
        <Route 
            path="/login" 
            element={
                <PublicOnlyRoute session={session}>
                    <Auth />
                </PublicOnlyRoute>
            } 
        />

        {/* Legal Routes */}
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />

        {/* Visitor Route (Always Public) */}
        <Route path="/l/:slug" element={<VisitorLock />} />

        {/* Protected Routes */}
        <Route 
            path="/dashboard" 
            element={
                <ProtectedRoute session={session}>
                    <Dashboard />
                </ProtectedRoute>
            } 
        />
        <Route 
            path="/lock/:id" 
            element={
                <ProtectedRoute session={session}>
                    <LockDetail />
                </ProtectedRoute>
            } 
        />
        <Route 
            path="/profile" 
            element={
                <ProtectedRoute session={session}>
                    <Profile />
                </ProtectedRoute>
            } 
        />
        <Route 
            path="/admin" 
            element={
                <ProtectedRoute session={session}>
                    <AdminDashboard />
                </ProtectedRoute>
            } 
        />
        
        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {isProduction && <Analytics />}
    </HashRouter>
  );
};

export default App;